import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Input } from '@monigo/ui';
import { SignupSchema, type SignupInput } from '@monigo/shared';
import { supabase } from '../../lib/supabase.js';
import { AuthLayout } from '../../components/Layout/AuthLayout.js';

interface PasswordStrength {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumberOrSpecial: boolean;
}

export function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumberOrSpecial: false,
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({ resolver: zodResolver(SignupSchema) });
  const passwordField = register('password');

  const password = watch('password');

  const updatePasswordStrength = (pwd: string) => {
    setPasswordStrength({
      minLength: pwd.length >= 8,
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
      hasNumberOrSpecial: /[0-9!@#$%^&*]/.test(pwd),
    });
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    updatePasswordStrength(e.target.value);
  };

  return (
    <AuthLayout title={t('auth.valueProposition')} subtitle={t('auth.valuePropositionSubtitle')}>
      <div className="space-y-5">
        <div className="space-y-2 text-center md:text-left">
          <h1 className="text-4xl font-display font-extrabold tracking-tight text-text">
            {t('auth.signupTitle')}
          </h1>
          <p className="text-base leading-7 text-text-muted">{t('auth.signupSubtitle')}</p>
        </div>

        <Card className="rounded-[1.75rem] p-5 md:p-6">
          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(async (values) => {
              setSubmitError(null);
              const { error } = await supabase.auth.signUp({
                email: values.email,
                password: values.password,
              });
              if (error) {
                setSubmitError(t('auth.errors.invalid'));
                return;
              }
              navigate('/confirm-pending');
            })}
          >
            <Field label={t('auth.email')} error={errors.email?.message}>
              <Input
                type="email"
                placeholder="name@example.com"
                autoComplete="email"
                hasError={!!errors.email}
                {...register('email')}
              />
            </Field>

            <Field label={t('auth.password')} error={errors.password?.message}>
              <Input
                type="password"
                placeholder="Create a secure password"
                autoComplete="new-password"
                hasError={!!errors.password}
                onChange={(e) => {
                  passwordField.onChange(e);
                  handlePasswordChange(e);
                }}
                name={passwordField.name}
                onBlur={passwordField.onBlur}
                ref={passwordField.ref}
              />
            </Field>

            {password ? (
              <div className="rounded-[1.25rem] bg-success/8 p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-success">
                  {t('auth.passwordRequirements')}
                </p>
                <div className="space-y-2">
                  <StrengthItem
                    met={passwordStrength.minLength}
                    label={t('auth.passwordReq8Chars')}
                  />
                  <StrengthItem
                    met={passwordStrength.hasUppercase && passwordStrength.hasLowercase}
                    label={t('auth.passwordReqUppercase')}
                  />
                  <StrengthItem
                    met={passwordStrength.hasNumberOrSpecial}
                    label={t('auth.passwordReqNumber')}
                  />
                </div>
              </div>
            ) : null}

            <CheckRow
              id="terms"
              label={
                <>
                  {t('auth.agreeToTerms')}{' '}
                  <a href="#" className="font-semibold text-primary">
                    {t('auth.termsOfService')}
                  </a>{' '}
                  and{' '}
                  <a href="#" className="font-semibold text-primary">
                    {t('auth.privacyPolicy')}
                  </a>
                </>
              }
              required
            />
            <CheckRow id="marketing" label={t('auth.marketingOptIn')} />

            {submitError ? (
              <p className="rounded-2xl bg-danger/10 p-3 text-sm text-danger">{submitError}</p>
            ) : null}

            <Button type="submit" isLoading={isSubmitting} className="w-full gap-2">
              {t('auth.signupCta')}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-text-muted">
          {t('auth.haveAccount')}{' '}
          <Link to="/login" className="font-semibold text-primary">
            {t('auth.loginCta')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

function StrengthItem({ met, label }: { met: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div
        className={`flex h-4 w-4 items-center justify-center rounded-full border ${
          met
            ? 'border-success bg-success/12 text-success'
            : 'border-border bg-surface text-text-muted'
        }`}
      >
        {met && <span>✓</span>}
      </div>
      <span className={met ? 'text-success' : 'text-text-muted'}>{label}</span>
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-2 text-sm">
      <span className="font-semibold text-text">{label}</span>
      {children}
      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </label>
  );
}

function CheckRow({ id, label, required }: { id: string; label: ReactNode; required?: boolean }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-sm text-text-muted">
      <input
        id={id}
        type="checkbox"
        required={required}
        className="mt-1 h-5 w-5 rounded-md border-border bg-surface-elevated"
      />
      <span>{label}</span>
    </label>
  );
}
